import React from "react";
import { PRIORITY_FILTER_OPTIONS } from "../../AllTickets/utils/constant";
import CalendarIcon from "../../../../../../../assets/svg/calendar-icon";
import FlaggedIcon from "../../../../../../../assets/svg/flagged-icon";
import TicketIcon from "../../../../../../../assets/svg/ticket-icon";
import UserSingleIcon from "../../../../../../../assets/svg/user-single-icon";
import TagsNewIcon from "../../../../../../../assets/svg/tags-new-icon";
import AgentsIcon from "../../../../../../../assets/svg/agents-icon";
import PMSPropertyManagementIcon from "../../../../../../../assets/svg/v1/pms-property-management-icon";
import CategoryIcon from "../../../../../../../assets/svg/category-icon";
import KeyAccountManagerIcon from "../../../../../../../assets/svg/key-account-manager-icon";
import { DEFAULT_DURATION_OPTIONS } from "../../../../../../../components/DurationPopper/utils/constants";
import VerifiedIcon from "../../../../../../../assets/svg/VerifiedIcon";
export type SharedWith = "private" | "everyone";

export const SHARED_WITH_OPTIONS: SharedWith[] = ["private", "everyone"];

export const SHARED_WITH_LABELS: Record<SharedWith, string> = {
  private: "Private",
  everyone: "Everyone",
};

export type FilterObjectType = "lead" | "deal";

export type FilterConditionUI = {
  key: string;
  comparison_operator: string;
  value: string[];
  is_custom_field: boolean;
  object_type?: FilterObjectType;
};

export type FilterGroupUI = {
  conditions: FilterConditionUI[];
  logical_operator: "and" | "or";
};

export type FilterFieldOption = {
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  iconClassName?: string;
  section?: FilterObjectType;
  insertAfterKey?: string;
};

export type ComparisonOperator = {
  key: string;
  label: string;
};

export const NATIVE_ARRAY_FIELDS = [
  "assignees",
  "priority",
  "tags",
  "ticket_category",
  "property_name",
];

export const NATIVE_STRING_FIELDS = [
  "created_by",
  "stage_key",
  "resolution_reason",
];

export const EXISTS_VALUE_OPTIONS = [
  { key: "true", label: "Yes" },
  { key: "false", label: "No" },
];

export const NATIVE_FILTER_FIELD_OPERATORS: Record<
  string,
  ComparisonOperator[]
> = {
  assignees: [
    { key: "contains", label: "Is" },
    { key: "not_contains", label: "Is not" },
    { key: "exists", label: "Exists" },
  ],
  created_by: [
    { key: "contains", label: "Is" },
    { key: "exists", label: "Exists" },
  ],
  duration: [
    { key: "equals", label: "Equals" },
    { key: "range", label: "Between" },
  ],
  priority: [
    { key: "contains", label: "Is" },
    { key: "not_contains", label: "Is not" },
    { key: "exists", label: "Exists" },
  ],
  request_support: [
    { key: "equals", label: "Is" },
    { key: "exists", label: "Exists" },
  ],
  property_name: [
    { key: "contains", label: "Is" },
    { key: "not_contains", label: "Is not" },
    { key: "exists", label: "Exists" },
  ],
  tags: [
    { key: "contains", label: "Is" },
    { key: "not_contains", label: "Is not" },
    { key: "exists", label: "Exists" },
  ],
  ticket_category: [
    { key: "contains", label: "Is" },
    { key: "not_contains", label: "Is not" },
    { key: "exists", label: "Exists" },
  ],
  stage_key: [
    { key: "contains", label: "Is" },
    { key: "exists", label: "Exists" },
  ],
  resolution_reason: [
    { key: "equals", label: "Is" },
    { key: "exists", label: "Exists" },
  ],
};

export const CUSTOM_FIELD_OPERATORS: Record<string, ComparisonOperator[]> = {
  dropdown_single_select: [
    { key: "equals", label: "Is" },
    { key: "exists", label: "Exists" },
  ],
  radio_single_select: [
    { key: "equals", label: "Is" },
    { key: "exists", label: "Exists" },
  ],
  dropdown_multi_select: [
    { key: "contains_any", label: "Has any" },
    { key: "contains_all", label: "Has all" },
    { key: "exists", label: "Exists" },
  ],
  checkbox: [
    { key: "equals", label: "Is" },
    { key: "contains_any", label: "Has any" },
    { key: "contains_all", label: "Has all" },
    { key: "exists", label: "Exists" },
  ],
  number: [
    { key: "equals", label: "Equals" },
    { key: "greater_than", label: "Greater than" },
    { key: "less_than", label: "Less than" },
    { key: "exists", label: "Exists" },
  ],
  text: [
    { key: "equals", label: "Equals" },
    { key: "contains", label: "Is" },
    { key: "exists", label: "Exists" },
  ],
  long_text: [
    { key: "equals", label: "Equals" },
    { key: "contains", label: "Is" },
    { key: "exists", label: "Exists" },
  ],
  date: [
    { key: "equals", label: "Equals" },
    { key: "range", label: "Between" },
  ],
};

export const FILTER_CONDITION_FIELD_PLACEHOLDER = "Select filter";
export const FILTER_CONDITION_OPERATOR_PLACEHOLDER = "Operator";
export const FILTER_CONDITION_VALUE_LOADING = "Loading...";

export const FILTER_VALUE_PLACEHOLDER_DEFAULT = "Select option";

export const NATIVE_FILTER_FIELD_VALUE_PLACEHOLDERS: Record<string, string> = {
  assignees: "Select assignees",
  created_by: "Select created by",
  duration: "Select duration",
  priority: "Select priority",
  request_support: "Select request support",
  property_name: "Select property name",
  tags: "Select tags",
  ticket_category: "Select ticket category",
  stage_key: "Select ticket status",
};

export const CUSTOM_FIELD_VALUE_PLACEHOLDERS: Record<string, string> = {
  dropdown_single_select: "Select option",
  radio_single_select: "Select option",
  dropdown_multi_select: "Select options",
  checkbox: "Select options",
  number: "Enter number",
  text: "Enter text",
  long_text: "Enter text",
  date: "Select date",
};

export const FILTER_NUMBER_VALUE_PLACEHOLDERS = {
  single: "Enter number",
  min: "Enter minimum number",
  max: "Enter maximum number",
} as const;

export const getFilterValuePlaceholder = (
  fieldKey: string,
  customFieldType?: string
): string => {
  if (!fieldKey) return FILTER_VALUE_PLACEHOLDER_DEFAULT;
  const nativePlaceholder = NATIVE_FILTER_FIELD_VALUE_PLACEHOLDERS[fieldKey];
  if (nativePlaceholder) return nativePlaceholder;
  const customPlaceholder = customFieldType
    ? CUSTOM_FIELD_VALUE_PLACEHOLDERS[customFieldType]
    : undefined;
  return customPlaceholder ?? FILTER_VALUE_PLACEHOLDER_DEFAULT;
};

export const CUSTOM_FIELD_KEY_OPERATORS_OVERRIDE: Record<
  string,
  ComparisonOperator[]
> = {
  "system-name": [
    { key: "equals", label: "Is" },
    { key: "not_contains", label: "Is not" },
    { key: "exists", label: "Exists" },
  ],
};

// Extra multi-select operators offered for native value-list fields, in
// addition to the base Is / Is not. These always render as a multi-select and
// send an array payload.
const HELPDESK_MULTI_SELECT_EXTRA_OPERATORS: ComparisonOperator[] = [
  { key: "contains_any", label: "Any of" },
  { key: "not_in", label: "None of" },
];

export const getOperatorsForFilterFieldKey = (
  fieldKey: string,
  customFieldType?: string
): ComparisonOperator[] => {
  if (!fieldKey) return [];
  const nativeOps = NATIVE_FILTER_FIELD_OPERATORS[fieldKey];
  if (nativeOps) {
    if (!NATIVE_ARRAY_FIELDS.includes(fieldKey)) return nativeOps;
    // Multi-select fields also support Any of / None of. Keep Exists last.
    const existsOps = nativeOps.filter((operator) => operator.key === "exists");
    const valueOps = nativeOps.filter((operator) => operator.key !== "exists");
    return [...valueOps, ...HELPDESK_MULTI_SELECT_EXTRA_OPERATORS, ...existsOps];
  }
  const overrideOps = CUSTOM_FIELD_KEY_OPERATORS_OVERRIDE[fieldKey];
  if (overrideOps) return overrideOps;
  const customOps = customFieldType
    ? CUSTOM_FIELD_OPERATORS[customFieldType]
    : undefined;
  return customOps || [];
};

export const getDefaultOperatorForFieldType = (fieldType?: string): string => {
  if (!fieldType) return "contains";
  const operators = CUSTOM_FIELD_OPERATORS[fieldType];
  return operators?.[0]?.key ?? "contains";
};

export const DURATION_OPTIONS = [
  { key: "last_7_days", label: "Last 7 Days" },
  { key: "last_30_days", label: "Last 30 Days" },
  { key: "last_3_months", label: "Last 3 Months" },
  { key: "last_6_months", label: "Last 6 Months" },
  { key: "last_1_year", label: "Last 1 Year" },
];

/** Between filter: same chip presets as `DurationPopper` defaults (no extra presets). */
export const HELPDESK_FILTER_BETWEEN_POPPER_OPTIONS = [
  ...DEFAULT_DURATION_OPTIONS,
];

export const PRIORITY_OPTIONS = PRIORITY_FILTER_OPTIONS;

export const REQUEST_SUPPORT_OPTIONS = [
  { key: "true", label: "Requested" },
  { key: "false", label: "Not Requested" },
];

export const VALUE_OPTIONS_MAP: Record<
  string,
  { key: string; label: string }[]
> = {
  duration: DURATION_OPTIONS,
  priority: PRIORITY_OPTIONS,
  request_support: REQUEST_SUPPORT_OPTIONS,
};

export const createEmptyCondition = (
  objectType: FilterObjectType = "lead"
): FilterConditionUI => ({
  key: "",
  comparison_operator: "contains",
  value: [],
  is_custom_field: false,
  object_type: objectType,
});

export const createEmptyGroup = (
  objectType: FilterObjectType = "lead"
): FilterGroupUI => ({
  conditions: [createEmptyCondition(objectType)],
  logical_operator: "and",
});

export interface ViewFormValues {
  name: string;
  scope: "" | "private" | "public";
  filter_groups: FilterGroupUI[];
  logical_operator: "and" | "or";
}

export const INITIAL_VIEW_FORM_VALUES: ViewFormValues = {
  name: "",
  scope: "",
  filter_groups: [],
  logical_operator: "and",
};

export function getDurationUTCValue(durationKey: string): string[] {
  const now = new Date();
  let startDate: Date;

  switch (durationKey) {
    case "last_7_days":
      startDate = new Date(now?.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case "last_30_days":
      startDate = new Date(now?.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
    case "last_3_months":
      startDate = new Date(now);
      startDate?.setMonth(startDate?.getMonth() - 3);
      break;
    case "last_6_months":
      startDate = new Date(now);
      startDate?.setMonth(startDate?.getMonth() - 6);
      break;
    case "last_1_year":
      startDate = new Date(now);
      startDate?.setFullYear(startDate?.getFullYear() - 1);
      break;
    default:
      return [];
  }

  return [startDate?.toISOString(), now?.toISOString()];
}

export const FILTER_FIELD_OPTIONS: FilterFieldOption[] = [
  { key: "assignees", label: "Assignees", icon: AgentsIcon },
  { key: "created_by", label: "Created By", icon: UserSingleIcon },
  { key: "duration", label: "Created Date", icon: CalendarIcon },
  { key: "priority", label: "Priority", icon: FlaggedIcon },
  {
    key: "request_support",
    label: "Request Support",
    icon: KeyAccountManagerIcon,
  },
  { key: "property_name", label: "Site Name", icon: PMSPropertyManagementIcon },
  { key: "tags", label: "Tags", icon: TagsNewIcon },
  { key: "ticket_category", label: "Ticket Category", icon: CategoryIcon },
  { key: "stage_key", label: "Ticket Status", icon: TicketIcon },
  { key: "resolution_reason", label: "Resolutions", icon: VerifiedIcon },
];
